import React from 'react';

/**
 * BookingsFilterBar — full filter set for the Admin → Bookings table.
 *
 * Filters supported (all optional, combine freely):
 *   • status[]              — confirmed / cancelled / completed / pending (multi)
 *   • dateFrom / dateTo     — session_date range (YYYY-MM-DD strings)
 *   • dateChip              — quick chip: today / week / month / future / past
 *                             (purely a UI shortcut — sets dateFrom/dateTo)
 *   • parentQuery           — substring match on parent name/email
 *   • childQuery            — substring match on child name
 *   • templateId            — exact template/session match
 *   • serviceType           — breakfast_club / after_school_club / holiday_club /
 *                             nursery_full_day / other
 *   • transport             — 'any' | 'yes' | 'no'
 *   • priceTier             — 'any' | 'flat' | 'early_bird' | 'last_minute'
 *   • centerId              — exact center match (only shown for multi-center)
 *   • search                — free-text across child name, parent email, booking id
 *   • sortBy                — 'newest' | 'oldest' | 'session_asc' | 'session_desc'
 *                             | 'price_asc' | 'price_desc'
 *
 * Status changes trigger a refetch (server-side). Everything else is
 * client-side filtering on the already-loaded list — fast and avoids
 * round-trips. Filter state is persisted into the URL query string
 * by the parent so a filtered view is shareable / bookmarkable.
 */

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed', color: '#10b981' },
  { value: 'pending',   label: 'Pending',   color: '#f59e0b' },
  { value: 'cancelled', label: 'Cancelled', color: '#ef4444' },
  { value: 'completed', label: 'Completed', color: '#6366f1' },
];

const SERVICE_OPTIONS = [
  { value: '',                    label: 'Any service' },
  { value: 'breakfast_club',      label: 'Breakfast Club' },
  { value: 'after_school_club',   label: 'After-School Club' },
  { value: 'holiday_club',        label: 'Holiday Club' },
  { value: 'nursery_full_day',    label: 'Nursery (Full Day)' },
  { value: 'other',               label: 'Other' },
];

const TRANSPORT_OPTIONS = [
  { value: 'any', label: 'Any transport' },
  { value: 'yes', label: '🚌 Requires transport' },
  { value: 'no',  label: 'No transport' },
];

const PRICE_TIER_OPTIONS = [
  { value: 'any',         label: 'Any price tier' },
  { value: 'flat',        label: '= Flat (base) price' },
  { value: 'early_bird',  label: '🐦 Early-bird (under base)' },
  { value: 'last_minute', label: '⏰ Last-minute (over base)' },
];

const SORT_OPTIONS = [
  { value: 'newest',       label: 'Newest booking first' },
  { value: 'oldest',       label: 'Oldest booking first' },
  { value: 'session_asc',  label: 'Session date ↑' },
  { value: 'session_desc', label: 'Session date ↓' },
  { value: 'price_asc',    label: 'Price ↑' },
  { value: 'price_desc',   label: 'Price ↓' },
];

export const DEFAULT_BOOKING_FILTERS = {
  status: ['confirmed'],
  dateFrom: '',
  dateTo: '',
  dateChip: '',
  parentQuery: '',
  childQuery: '',
  templateId: '',
  serviceType: '',
  transport: 'any',
  priceTier: 'any',
  centerId: '',
  search: '',
  sortBy: 'newest',
};

function chipDates(chip) {
  if (!chip) return { dateFrom: '', dateTo: '' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  if (chip === 'today') {
    return { dateFrom: iso(today), dateTo: iso(today) };
  }
  if (chip === 'week') {
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((day + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { dateFrom: iso(monday), dateTo: iso(sunday) };
  }
  if (chip === 'month') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { dateFrom: iso(first), dateTo: iso(last) };
  }
  if (chip === 'future') {
    return { dateFrom: iso(today), dateTo: '' };
  }
  if (chip === 'past') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return { dateFrom: '', dateTo: iso(yesterday) };
  }
  return { dateFrom: '', dateTo: '' };
}

/**
 * Match a booking's `service_type` against a filter's selected service.
 *
 * The two taxonomies don't line up 1:1:
 *   - The filter dropdown uses enrollment-taxonomy values
 *     (`breakfast_club`, `after_school_club`, `holiday_club`,
 *     `nursery_full_day`, `other`).
 *   - Bookings inherit their `service_type` from session templates,
 *     which use a more granular set (e.g. `afterschool_with_pickup`,
 *     `afterschool_walking`, `afterschool_plus_home`, `nursery_all_day`,
 *     `breakfast`, `breakfast_club`, `holiday_club`, `holiday_week`…).
 *
 * The previous filter used strict `===` equality which meant selecting
 * "After-School Club" returned zero rows — bookings actually stored
 * `afterschool_*` variants, never the enrollment string. We now match
 * on prefix families so the filter behaves as a user would expect.
 */
export function bookingMatchesServiceFilter(bookingServiceType, filterValue) {
  if (!filterValue) return true;                 // "Any service" — pass
  if (!bookingServiceType) return false;         // Booking has no type — can't match a specific one
  const bt = String(bookingServiceType).toLowerCase();
  const fv = String(filterValue).toLowerCase();
  // Direct equality wins fast (covers legacy bookings that already
  // stored the enrollment-style value).
  if (bt === fv) return true;
  // Family matching — bookings that share the same "kind" as the
  // selected filter category count as matches. Keep this list next to
  // SERVICE_OPTIONS so any new filter option is easy to slot in.
  const FAMILY_PREFIXES = {
    breakfast_club:    ['breakfast'],
    after_school_club: ['afterschool', 'after_school', 'aftercare'],
    holiday_club:      ['holiday'],
    nursery_full_day:  ['nursery'],
    other:             ['other'],
  };
  const prefixes = FAMILY_PREFIXES[fv] || [fv];
  return prefixes.some(p => bt === p || bt.startsWith(`${p}_`));
}

export function applyBookingFilters(bookings, filters) {
  if (!Array.isArray(bookings)) return [];
  const f = { ...DEFAULT_BOOKING_FILTERS, ...filters };
  const q = (s) => (s || '').toString().trim().toLowerCase();
  const searchTerm = q(f.search);
  const parentTerm = q(f.parentQuery);
  const childTerm = q(f.childQuery);

  let from = null;
  let to = null;
  if (f.dateFrom) {
    from = new Date(`${f.dateFrom}T00:00:00`);
  }
  if (f.dateTo) {
    to = new Date(`${f.dateTo}T23:59:59`);
  }

  const out = bookings.filter((b) => {
    // status (server may already have filtered, but be defensive)
    if (f.status && f.status.length > 0 && !f.status.includes(b.status)) return false;

    // date range
    if (from || to) {
      const sd = b.session_date ? new Date(b.session_date) : null;
      if (!sd || isNaN(sd.getTime())) return false;
      if (from && sd < from) return false;
      if (to && sd > to) return false;
    }

    // parent search
    if (parentTerm) {
      const pn = q(b.parent_name);
      const pe = q(b.parent_email);
      if (!pn.includes(parentTerm) && !pe.includes(parentTerm)) return false;
    }

    // child search
    if (childTerm) {
      const cn = q(b.child_name);
      if (!cn.includes(childTerm)) return false;
    }

    // template
    if (f.templateId && b.template_id !== f.templateId) return false;

    // service type — smart match across enrolment/session-template taxonomies.
    if (f.serviceType && !bookingMatchesServiceFilter(b.service_type, f.serviceType)) return false;

    // transport
    if (f.transport === 'yes' && !b.requires_transport) return false;
    if (f.transport === 'no' && !!b.requires_transport) return false;

    // price tier (compare price_paid vs base_price; tolerate floating drift)
    if (f.priceTier && f.priceTier !== 'any') {
      const paid = Number(b.price_paid || 0);
      const base = Number(b.base_price || 0);
      if (!base) return false;
      const delta = paid - base;
      if (f.priceTier === 'flat' && Math.abs(delta) > 0.01) return false;
      if (f.priceTier === 'early_bird' && delta >= -0.01) return false;
      if (f.priceTier === 'last_minute' && delta <= 0.01) return false;
    }

    // center
    if (f.centerId && b.center_id !== f.centerId) return false;

    // free-text search
    if (searchTerm) {
      const haystack = [
        b.child_name,
        b.parent_email,
        b.parent_name,
        b.session_name,
        b._id,
      ].map(q).join(' | ');
      if (!haystack.includes(searchTerm)) return false;
    }

    return true;
  });

  // sort
  const safeDate = (v) => (v ? new Date(v).getTime() : 0);
  switch (f.sortBy) {
    case 'oldest':
      out.sort((a, b) => safeDate(a.created_at) - safeDate(b.created_at));
      break;
    case 'session_asc':
      out.sort((a, b) => safeDate(a.session_date) - safeDate(b.session_date));
      break;
    case 'session_desc':
      out.sort((a, b) => safeDate(b.session_date) - safeDate(a.session_date));
      break;
    case 'price_asc':
      out.sort((a, b) => Number(a.price_paid || 0) - Number(b.price_paid || 0));
      break;
    case 'price_desc':
      out.sort((a, b) => Number(b.price_paid || 0) - Number(a.price_paid || 0));
      break;
    case 'newest':
    default:
      out.sort((a, b) => safeDate(b.created_at) - safeDate(a.created_at));
      break;
  }

  return out;
}

export function countActiveBookingFilters(filters) {
  const f = { ...DEFAULT_BOOKING_FILTERS, ...filters };
  let n = 0;
  if (Array.isArray(f.status) && (f.status.length !== 1 || f.status[0] !== 'confirmed')) n += 1;
  if (f.dateFrom || f.dateTo) n += 1;
  if (f.parentQuery) n += 1;
  if (f.childQuery) n += 1;
  if (f.templateId) n += 1;
  if (f.serviceType) n += 1;
  if (f.transport && f.transport !== 'any') n += 1;
  if (f.priceTier && f.priceTier !== 'any') n += 1;
  if (f.centerId) n += 1;
  if (f.search) n += 1;
  if (f.sortBy && f.sortBy !== 'newest') n += 1;
  return n;
}

/**
 * Render the full filter bar. `filters` and `onChange(next)` are required;
 * `templates`, `centers`, `onReset` are optional but recommended.
 *
 * `centers` should be an array of {center_id, name}; the center filter
 * is auto-hidden when fewer than 2 are supplied.
 */
export function BookingsFilterBar({
  filters,
  onChange,
  templates = [],
  centers = [],
  totalCount = 0,
  filteredCount = 0,
  onReset,
  onUseInReprice,
}) {
  const f = { ...DEFAULT_BOOKING_FILTERS, ...filters };

  const update = (patch) => {
    const next = { ...f, ...patch };
    onChange(next);
  };

  const toggleStatus = (value) => {
    const cur = new Set(f.status || []);
    if (cur.has(value)) cur.delete(value); else cur.add(value);
    const next = Array.from(cur);
    update({ status: next.length ? next : ['confirmed'] });
  };

  const setChip = (chip) => {
    if (f.dateChip === chip) {
      update({ dateChip: '', dateFrom: '', dateTo: '' });
    } else {
      update({ dateChip: chip, ...chipDates(chip) });
    }
  };

  const activeCount = countActiveBookingFilters(f);
  const showCenters = Array.isArray(centers) && centers.length >= 2;

  return (
    <div
      className="card"
      style={{ marginBottom: '1rem', padding: '0.85rem 1rem' }}
      data-testid="bookings-filter-bar"
    >
      {/* Row 1 — search + sort + reset */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="🔎 Search child, parent email, session or booking id"
          value={f.search}
          onChange={(e) => update({ search: e.target.value })}
          data-testid="bookings-filter-search"
          style={{
            flex: '1 1 260px',
            minWidth: 0,
            padding: '0.5rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.9rem',
          }}
        />
        <select
          value={f.sortBy}
          onChange={(e) => update({ sortBy: e.target.value })}
          data-testid="bookings-filter-sort"
          style={selectStyle}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span
          style={{
            fontSize: '0.85rem',
            color: '#374151',
            background: '#f3f4f6',
            padding: '0.35rem 0.65rem',
            borderRadius: '999px',
            fontWeight: 600,
          }}
          data-testid="bookings-filter-count"
        >
          {filteredCount} of {totalCount}
        </span>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            data-testid="bookings-filter-reset"
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
            title={`Clear ${activeCount} active filter${activeCount === 1 ? '' : 's'}`}
          >
            ✕ Reset ({activeCount})
          </button>
        )}
        {typeof onUseInReprice === 'function' && (
          <button
            type="button"
            onClick={onUseInReprice}
            data-testid="bookings-filter-use-in-reprice"
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #00BCD4',
              borderRadius: '6px',
              background: '#fff',
              color: '#00BCD4',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
            title="Open the bulk-reprice dialog pre-filled with these filters"
          >
            💱 Reprice these…
          </button>
        )}
      </div>

      {/* Row 2 — status chips */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.75rem' }}>
        <span style={labelStyle}>Status:</span>
        {STATUS_OPTIONS.map((s) => {
          const on = (f.status || []).includes(s.value);
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => toggleStatus(s.value)}
              data-testid={`bookings-filter-status-${s.value}`}
              style={{
                padding: '0.3rem 0.7rem',
                borderRadius: '999px',
                border: `1px solid ${on ? s.color : '#d1d5db'}`,
                background: on ? s.color : '#fff',
                color: on ? '#fff' : '#374151',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 150ms ease, color 150ms ease',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Row 3 — date range + chips */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.5rem' }}>
        <span style={labelStyle}>Session date:</span>
        <input
          type="date"
          value={f.dateFrom}
          onChange={(e) => update({ dateFrom: e.target.value, dateChip: '' })}
          data-testid="bookings-filter-date-from"
          style={dateInputStyle}
        />
        <span style={{ color: '#9ca3af' }}>→</span>
        <input
          type="date"
          value={f.dateTo}
          onChange={(e) => update({ dateTo: e.target.value, dateChip: '' })}
          data-testid="bookings-filter-date-to"
          style={dateInputStyle}
        />
        {[
          { v: 'today',  l: 'Today' },
          { v: 'week',   l: 'This week' },
          { v: 'month',  l: 'This month' },
          { v: 'future', l: 'Future' },
          { v: 'past',   l: 'Past' },
        ].map((c) => {
          const on = f.dateChip === c.v;
          return (
            <button
              key={c.v}
              type="button"
              onClick={() => setChip(c.v)}
              data-testid={`bookings-filter-chip-${c.v}`}
              style={chipStyle(on)}
            >
              {c.l}
            </button>
          );
        })}
      </div>

      {/* Row 4 — parent / child / template / service */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem', marginTop: '0.75rem' }}>
        <input
          type="text"
          placeholder="👤 Parent name or email"
          value={f.parentQuery}
          onChange={(e) => update({ parentQuery: e.target.value })}
          data-testid="bookings-filter-parent"
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="🧒 Child name"
          value={f.childQuery}
          onChange={(e) => update({ childQuery: e.target.value })}
          data-testid="bookings-filter-child"
          style={inputStyle}
        />
        <select
          value={f.templateId}
          onChange={(e) => update({ templateId: e.target.value })}
          data-testid="bookings-filter-template"
          style={selectStyle}
        >
          <option value="">Any session template</option>
          {(templates || []).map((t) => (
            <option key={t._id} value={t._id}>{t.name}</option>
          ))}
        </select>
        <select
          value={f.serviceType}
          onChange={(e) => update({ serviceType: e.target.value })}
          data-testid="bookings-filter-service-type"
          style={selectStyle}
        >
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={f.transport}
          onChange={(e) => update({ transport: e.target.value })}
          data-testid="bookings-filter-transport"
          style={selectStyle}
        >
          {TRANSPORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={f.priceTier}
          onChange={(e) => update({ priceTier: e.target.value })}
          data-testid="bookings-filter-price-tier"
          style={selectStyle}
        >
          {PRICE_TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {showCenters && (
          <select
            value={f.centerId}
            onChange={(e) => update({ centerId: e.target.value })}
            data-testid="bookings-filter-center"
            style={selectStyle}
          >
            <option value="">Any center</option>
            {centers.map((c) => (
              <option key={c.center_id} value={c.center_id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

const selectStyle = {
  padding: '0.4rem 0.6rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  background: '#fff',
  fontSize: '0.85rem',
  cursor: 'pointer',
  minWidth: 0,
};

const inputStyle = {
  padding: '0.4rem 0.6rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.85rem',
  minWidth: 0,
};

const dateInputStyle = {
  padding: '0.4rem 0.6rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.85rem',
  minWidth: 0,
};

const labelStyle = {
  fontSize: '0.8rem',
  color: '#6b7280',
  fontWeight: 600,
};

const chipStyle = (on) => ({
  padding: '0.3rem 0.7rem',
  borderRadius: '999px',
  border: `1px solid ${on ? '#00BCD4' : '#d1d5db'}`,
  background: on ? '#00BCD4' : '#fff',
  color: on ? '#fff' : '#374151',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
});

// ---------------------------------------------------------------------------
// URL persistence helpers (used by AdminDashboard)
// ---------------------------------------------------------------------------

/**
 * Read filters from window.location.search, falling back to defaults.
 * Designed for URL-shareable filtered views.
 */
export function readBookingFiltersFromURL() {
  if (typeof window === 'undefined') return { ...DEFAULT_BOOKING_FILTERS };
  const sp = new URLSearchParams(window.location.search);
  const out = { ...DEFAULT_BOOKING_FILTERS };
  const status = sp.get('status');
  if (status) {
    const parts = status.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length) out.status = parts;
  }
  for (const k of [
    'dateFrom', 'dateTo', 'dateChip', 'parentQuery', 'childQuery',
    'templateId', 'serviceType', 'transport', 'priceTier', 'centerId',
    'search', 'sortBy',
  ]) {
    const v = sp.get(k);
    if (v !== null && v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * Write the current filter set into the URL search params, replacing
 * the current entry (no history pollution).
 */
export function writeBookingFiltersToURL(filters) {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams();
  const f = { ...DEFAULT_BOOKING_FILTERS, ...filters };
  if (!(f.status.length === 1 && f.status[0] === 'confirmed')) {
    sp.set('status', (f.status || []).join(','));
  }
  for (const k of [
    'dateFrom', 'dateTo', 'dateChip', 'parentQuery', 'childQuery',
    'templateId', 'serviceType', 'centerId', 'search',
  ]) {
    if (f[k]) sp.set(k, f[k]);
  }
  if (f.transport && f.transport !== 'any') sp.set('transport', f.transport);
  if (f.priceTier && f.priceTier !== 'any') sp.set('priceTier', f.priceTier);
  if (f.sortBy && f.sortBy !== 'newest') sp.set('sortBy', f.sortBy);

  const qs = sp.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}

export default BookingsFilterBar;
